<template>
    <div ref="referenceElement" class="ui-float-dropdown">
        <Float
            :placement="placement"
            :offset="offset"
            composable
            :portal="portal"
            :enter="enter"
            :enter-from="enterFrom"
            :enter-to="enterTo"
            :leave="leave"
            :leave-from="leaveFrom"
            :leave-to="leaveTo"
            :show="show"
            :middleware="middleware"
        >
            <FloatReference>
                <div
                    class="ui-float-dropdown-reference"
                    :class="{
                        'ui-float-dropdown-reference--active': show,
                    }"
                    @click="toggle"
                >
                    <slot name="reference"></slot>
                </div>
            </FloatReference>

            <FloatContent>
                <ui-popover :class="popoverClass" class="ui-float-dropdown-content" @click-outside="handleClickOutside">
                    <slot />
                </ui-popover>
            </FloatContent>
        </Float>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Float, FloatContent, FloatReference } from '@headlessui-float/vue';
import type { Placement } from '@floating-ui/vue';
import { flip, offset, shift, size } from '@floating-ui/dom';

interface Props {
    show?: boolean;
    placement?: Placement;
    offset?: number;
    portal?: boolean;
    popoverClass?: string;
    /* When false, the panel keeps its natural width instead of matching the
     * reference element (useful for icon-button triggers like the nav cluster). */
    matchReferenceWidth?: boolean;
    enter?: string;
    enterFrom?: string;
    enterTo?: string;
    leave?: string;
    leaveFrom?: string;
    leaveTo?: string;
}

const props = withDefaults(defineProps<Props>(), {
    placement: 'bottom-start',
    offset: 0,
    portal: false,
    popoverClass: '',
    matchReferenceWidth: true,
    enter: 'transition duration-300 ease-in-out',
    enterFrom: 'opacity-0 -translate-y-1',
    enterTo: 'opacity-100 translate-y-0',
    leave: 'transition duration-300 ease-in-out',
    leaveFrom: 'opacity-100 translate-y-0',
    leaveTo: 'opacity-0 -translate-y-1',
    show: false,
});

const emit = defineEmits<{
    (event: 'opened'): void;
    (event: 'closed'): void;
}>();

const show = ref(props.show);

/**
 * Reference to the toggle button element
 */
const referenceElement = ref<HTMLElement | null>(null);

/**
 * Toggle dropdown visibility
 */
const toggle = () => {
    show.value = !show.value;
};

/**
 * Handle click outside - close dropdown only if click is not on reference element
 */
const handleClickOutside = (event: MouseEvent) => {
    // Check if click is on the reference element (toggle button)
    if (referenceElement.value?.contains(event.target as Node)) {
        return;
    }
    show.value = false;
};

watch(show, (newValue) => {
    if (newValue) {
        emit('opened');
    } else {
        emit('closed');
    }
});

/* flip + shift keep the panel inside the viewport — an end-of-row trigger
 * (nav profile) would otherwise push it past the edge and create a
 * horizontal scrollbar. */
const middleware = computed(() => {
    const list = [
        offset({
            mainAxis: 0,
            crossAxis: 0,
        }),
        flip(),
        shift({ padding: 8 }),
    ];

    if (props.matchReferenceWidth) {
        list.push(
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`,
                    });
                },
            }),
        );
    }

    return list;
});
</script>
